/* eslint-disable */
import React, { useState, useRef, useCallback, useEffect } from 'react';

const RichTextEditor = function(props) {
	const value = props.value || '';
	const onChange = props.onChange;
	const maxLength = props.maxLength;
	
	const [content, setContent] = useState(value);
	const [showLinkDialog, setShowLinkDialog] = useState(false);
	const [linkUrl, setLinkUrl] = useState('');
	const [linkText, setLinkText] = useState('');
	const [savedSelection, setSavedSelection] = useState(null);
	const [activeFormats, setActiveFormats] = useState({
		bold: false,
		italic: false,
		underline: false
	});
	const editorRef = useRef(null);

	// Check which formats are currently active
	const checkActiveFormats = useCallback(function() {
		if (!editorRef.current) return;
		
		try {
			const formats = {
				bold: document.queryCommandState('bold'),
				italic: document.queryCommandState('italic'),
				underline: document.queryCommandState('underline')
			};
			setActiveFormats(formats);
		} catch (e) {
			// Fallback if queryCommandState fails
			console.warn('queryCommandState failed:', e);
		}
	}, []);

	// Update content when value prop changes
	useEffect(function() {
		if (value !== content && editorRef.current) {
			const cursorPosition = saveCursorPosition();
			setContent(value);
			editorRef.current.innerHTML = value;
			setTimeout(function() {
				restoreCursorPosition(cursorPosition);
			}, 0);
		}
	}, [value, content]);

	// Initialize content when component mounts
	useEffect(function() {
		if (editorRef.current && content) {
			editorRef.current.innerHTML = content;
		}
	}, []);

	// Save and restore cursor position
	const saveCursorPosition = function() {
		const selection = window.getSelection();
		if (selection && selection.rangeCount > 0 && editorRef.current) {
			const range = selection.getRangeAt(0);
			const preCaretRange = range.cloneRange();
			preCaretRange.selectNodeContents(editorRef.current);
			preCaretRange.setEnd(range.endContainer, range.endOffset);
			return preCaretRange.toString().length;
		}
		return 0;
	};

	const restoreCursorPosition = function(position) {
		if (!editorRef.current) return;
		
		const selection = window.getSelection();
		const range = document.createRange();
		let charIndex = 0;
		let nodeStack = [editorRef.current];
		let foundStart = false;

		while (nodeStack.length > 0 && !foundStart) {
			const node = nodeStack.pop();
			
			if (node.nodeType === Node.TEXT_NODE) {
				const nextCharIndex = charIndex + node.textContent.length;
				if (position >= charIndex && position <= nextCharIndex) {
					range.setStart(node, position - charIndex);
					range.setEnd(node, position - charIndex);
					foundStart = true;
				}
				charIndex = nextCharIndex;
			} else {
				for (let i = node.childNodes.length - 1; i >= 0; i--) {
					nodeStack.push(node.childNodes[i]);
				}
			}
		}

		if (foundStart) {
			selection.removeAllRanges();
			selection.addRange(range);
		}
	};

	// Save current selection for link insertion
	const saveSelection = function() {
		const selection = window.getSelection();
		if (selection.rangeCount > 0) {
			return selection.getRangeAt(0).cloneRange();
		}
		return null;
	};

	// Restore saved selection
	const restoreSelection = function(range) {
		if (range) {
			const selection = window.getSelection();
			selection.removeAllRanges();
			selection.addRange(range);
		}
	};

	// Handle formatting commands
	const handleFormat = useCallback(function(command, value) {
		if (value === undefined) {
			value = null;
		}
		if (command === 'insertUnorderedList') {
			// Focus the editor first to ensure proper context
			if (editorRef.current) {
				editorRef.current.focus();
			}
		}
		document.execCommand(command, false, value);
		if (editorRef.current) {
			editorRef.current.focus();
		}
		// Check active formats after command execution
		setTimeout(checkActiveFormats, 0);
	}, [checkActiveFormats]);

	// Handle content change
	const handleContentChange = useCallback(function() {
		if (editorRef.current) {
			const newContent = editorRef.current.innerHTML;
			setContent(newContent);
			if (onChange) {
				onChange(newContent);
			}
			// Check active formats after content change
			setTimeout(checkActiveFormats, 0);
		}
	}, [onChange, checkActiveFormats]);

	// Handle cursor movement and selection changes
	const handleSelectionChange = useCallback(function() {
		checkActiveFormats();
	}, [checkActiveFormats]);

	// Add event listeners for selection changes
	useEffect(function() {
		const editor = editorRef.current;
		if (editor) {
			editor.addEventListener('keyup', handleSelectionChange);
			editor.addEventListener('mouseup', handleSelectionChange);
			editor.addEventListener('focus', handleSelectionChange);
			
			return function() {
				editor.removeEventListener('keyup', handleSelectionChange);
				editor.removeEventListener('mouseup', handleSelectionChange);
				editor.removeEventListener('focus', handleSelectionChange);
			};
		}
	}, [handleSelectionChange]);

	// Open link dialog and save current selection
	const openLinkDialog = function() {
		const selection = saveSelection();
		setSavedSelection(selection);
		setShowLinkDialog(true);
		
		// If there's selected text, use it as default link text
		if (selection && !selection.collapsed) {
			const selectedText = selection.toString();
			setLinkText(selectedText);
		}
	};

	// Insert link
	const insertLink = useCallback(function() {
		if (linkUrl && linkText) {
			// Focus the editor and restore selection
			if (editorRef.current) {
				editorRef.current.focus();
			}
			
			if (savedSelection) {
				restoreSelection(savedSelection);
			}
			
			// Create link HTML
			const linkHtml = '<a href="' + linkUrl + '" target="_blank" rel="noopener noreferrer">' + linkText + '</a>';
			
			// Insert the link
			if (savedSelection && !savedSelection.collapsed) {
				// Replace selected text with link
				savedSelection.deleteContents();
				const linkElement = document.createElement('div');
				linkElement.innerHTML = linkHtml;
				savedSelection.insertNode(linkElement.firstChild);
			} else {
				// Insert link at cursor position or at the end
				document.execCommand('insertHTML', false, linkHtml);
			}
			
			// Clean up and close dialog
			setShowLinkDialog(false);
			setLinkUrl('');
			setLinkText('');
			setSavedSelection(null);
			
			// Trigger content change
			handleContentChange();
			
			// Focus back to editor
			setTimeout(function() {
				if (editorRef.current) {
					editorRef.current.focus();
				}
			}, 0);
		}
	}, [linkUrl, linkText, savedSelection, handleContentChange]);

	// Format toolbar button component
	const FormatButton = function(props) {
		const onClick = props.onClick;
		const text = props.text;
		const title = props.title;
		const isActive = props.isActive || false;
		const buttonType = props.buttonType || 'default';
		
		const getButtonStyles = function() {
			let isCurrentlyActive = false;
			if (buttonType === 'bold') {
				isCurrentlyActive = activeFormats.bold;
			} else if (buttonType === 'italic') {
				isCurrentlyActive = activeFormats.italic;
			} else if (buttonType === 'underline') {
				isCurrentlyActive = activeFormats.underline;
			}

			const baseStyle = {
				height: '36px',
				minWidth: '40px',
				padding: '8px 12px',
				border: '1px solid #000',
				fontSize: '14px',
				fontWeight: 'bold',
				cursor: 'pointer',
				transition: 'all 0.2s ease',
				backgroundColor: isCurrentlyActive ? '#1F61A5' : '#fff',
				color: isCurrentlyActive ? '#fff' : '#000',
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				boxSizing: 'border-box',
				outline: 'none'
			};

			if (buttonType === 'italic') {
				return Object.assign({}, baseStyle, { fontStyle: 'italic' });
			} else if (buttonType === 'underline') {
				return Object.assign({}, baseStyle, { textDecoration: 'underline' });
			} else {
				return baseStyle;
			}
		};

		const handleMouseEnter = function(e) {
			let isCurrentlyActive = false;
			if (buttonType === 'bold') {
				isCurrentlyActive = activeFormats.bold;
			} else if (buttonType === 'italic') {
				isCurrentlyActive = activeFormats.italic;
			} else if (buttonType === 'underline') {
				isCurrentlyActive = activeFormats.underline;
			}

			if (!isCurrentlyActive) {
				e.target.style.backgroundColor = '#000';
				e.target.style.color = '#fff';
			}
		};

		const handleMouseLeave = function(e) {
			let isCurrentlyActive = false;
			if (buttonType === 'bold') {
				isCurrentlyActive = activeFormats.bold;
			} else if (buttonType === 'italic') {
				isCurrentlyActive = activeFormats.italic;
			} else if (buttonType === 'underline') {
				isCurrentlyActive = activeFormats.underline;
			}

			if (!isCurrentlyActive) {
				e.target.style.backgroundColor = '#fff';
				e.target.style.color = '#000';
			} else {
				e.target.style.backgroundColor = '#007bff';
				e.target.style.color = '#fff';
			}
		};

		return React.createElement('button', {
			type: 'button',
			onClick: onClick,
			title: title,
			style: getButtonStyles(),
			onMouseEnter: handleMouseEnter,
			onMouseLeave: handleMouseLeave
		}, text);
	};

	const toolbarStyle = { 
		display: 'flex', 
		alignItems: 'center', 
		gap: '0px', 
		marginBottom: '0px',
		border: '1px solid grey',
		borderBottom: 'none',
		padding: '8px',
		backgroundColor: '#f8f9fa'
	};

	const editorStyle = {
		minHeight: '120px',
		maxHeight: '300px',
		overflowY: 'auto',
		border: '1px solid grey',
		borderTop: 'none',
		padding: '12px',
		fontSize: '14px',
		lineHeight: '1.5',
		outline: 'none',
		backgroundColor: '#fff'
	};

	const handleSelectMouseEnter = function(e) {
		e.target.style.backgroundColor = '#000';
		e.target.style.color = '#fff';
	};

	const handleSelectMouseLeave = function(e) {
		e.target.style.backgroundColor = '#fff';
		e.target.style.color = '#000';
	};

	const handleSelectChange = function(e) {
		handleFormat('fontSize', e.target.value);
	};

	const modalOverlayStyle = {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000
	};

	const modalContentStyle = {
		backgroundColor: 'white',
		padding: '24px',
		borderRadius: '8px',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
		width: '400px',
		maxWidth: '90vw'
	};

	const modalTitleStyle = { 
		fontSize: '18px', 
		fontWeight: '600', 
		marginBottom: '16px', 
		margin: '0 0 16px 0' 
	};

	const labelStyle = { 
		display: 'block', 
		fontSize: '14px', 
		fontWeight: '500', 
		color: 'black', 
		marginBottom: '4px' 
	};

	const inputStyle = {
		width: '100%',
		padding: '8px',
		border: '1px solid #d1d5db',
		borderRadius: '4px',
		fontSize: '14px',
		outline: 'none',
		boxSizing: 'border-box'
	};

	const buttonContainerStyle = { 
		display: 'flex', 
		gap: '8px', 
		justifyContent: 'flex-end' 
	};

	const cancelButtonStyle = {
		padding: '8px 16px',
		color: '#6b7280',
		border: '1px solid #d1d5db',
		borderRadius: '4px',
		backgroundColor: 'white',
		cursor: 'pointer',
		fontSize: '14px'
	};

	const insertButtonStyle = {
		padding: '8px 16px',
		backgroundColor: !linkUrl || !linkText ? '#d1d5db' : '#1F61A5',
		color: 'white',
		border: 'none',
		borderRadius: '4px',
		cursor: !linkUrl || !linkText ? 'not-allowed' : 'pointer',
		fontSize: '14px'
	};

	const handleCancelClick = function() {
		setShowLinkDialog(false);
		setLinkUrl('');
		setLinkText('');
		setSavedSelection(null);
	};

	const handleLinkTextChange = function(e) {
		setLinkText(e.target.value);
	};

	const handleLinkUrlChange = function(e) {
		setLinkUrl(e.target.value);
	};

	const handleInputFocus = function(e) {
		e.target.style.borderColor = '#3b82f6';
	};

	const handleInputBlur = function(e) {
		e.target.style.borderColor = '#d1d5db';
	};

	const handleCancelMouseEnter = function(e) {
		e.target.style.backgroundColor = '#f9fafb';
	};

	const handleCancelMouseLeave = function(e) {
		e.target.style.backgroundColor = 'white';
	};

	const handleInsertMouseEnter = function(e) {
		if (linkUrl && linkText) {
			e.target.style.backgroundColor = '#2563eb';
		}
	};

	const handleInsertMouseLeave = function(e) {
		if (linkUrl && linkText) {
			e.target.style.backgroundColor = '#3b82f6';
		}
	};

	const characterCount = editorRef.current && editorRef.current.textContent ? editorRef.current.textContent.length : 0;

	return React.createElement('div', null,
		// Formatting Toolbar
		React.createElement('div', { style: toolbarStyle },
			React.createElement(FormatButton, {
				onClick: function() { handleFormat('bold'); },
				text: 'B',
				title: 'Bold (Ctrl+B)',
				buttonType: 'bold'
			}),
			React.createElement(FormatButton, {
				onClick: function() { handleFormat('italic'); },
				text: 'I',
				title: 'Italic (Ctrl+I)',
				buttonType: 'italic'
			}),
			React.createElement(FormatButton, {
				onClick: function() { handleFormat('underline'); },
				text: 'U',
				title: 'Underline (Ctrl+U)',
				buttonType: 'underline'
			}),
			React.createElement(FormatButton, {
				onClick: function() { handleFormat('insertUnorderedList'); },
				text: '• List',
				title: 'Bullet Points',
				buttonType: 'list'
			}),
			React.createElement(FormatButton, {
				onClick: openLinkDialog,
				text: '🔗 Link',
				title: 'Insert Link',
				buttonType: 'link'
			}),
		),
		
		// Rich Text Editor
		React.createElement('div', {
			ref: editorRef,
			contentEditable: true,
			onInput: handleContentChange,
			style: editorStyle,
			suppressContentEditableWarning: true
		}),
		
		// Character count
		React.createElement('small', { 
			style: { color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' } 
		}, 'Characters: ' + characterCount + (maxLength ? ' / ' + maxLength : '')),

		// Link Dialog
		showLinkDialog && React.createElement('div', { style: modalOverlayStyle },
			React.createElement('div', { style: modalContentStyle },
				React.createElement('h3', { style: modalTitleStyle }, 'Insert Link'),
				React.createElement('div', { style: { marginBottom: '16px' } },
					React.createElement('label', { style: labelStyle }, 'Link Text'),
					React.createElement('input', {
						type: 'text',
						value: linkText,
						onChange: handleLinkTextChange,
						style: inputStyle,
						placeholder: 'Enter link text',
						onFocus: handleInputFocus,
						onBlur: handleInputBlur
					})
				),
				React.createElement('div', { style: { marginBottom: '20px' } },
					React.createElement('label', { style: labelStyle }, 'URL'),
					React.createElement('input', {
						type: 'url',
						value: linkUrl,
						onChange: handleLinkUrlChange,
						style: inputStyle,
						placeholder: 'https://example.com',
						onFocus: handleInputFocus,
						onBlur: handleInputBlur
					})
				),
				React.createElement('div', { style: buttonContainerStyle },
					React.createElement('button', {
						onClick: handleCancelClick,
						style: cancelButtonStyle,
						onMouseEnter: handleCancelMouseEnter,
						onMouseLeave: handleCancelMouseLeave
					}, 'Cancel'),
					React.createElement('button', {
						onClick: insertLink,
						disabled: !linkUrl || !linkText,
						style: insertButtonStyle,
						onMouseEnter: handleInsertMouseEnter,
						onMouseLeave: handleInsertMouseLeave
					}, 'Insert Link')
				)
			)
		)
	);
};

export default RichTextEditor;